import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOnce, useRouteName } from '../../../../hooks';
import useActiveProvider from '../../../../hooks/useActiveProvider';
import { IProviderClient } from '../../../../api/Providers/Clients/types';
import { PRO_CLIENT_LIST } from '../../../../route-names';
import store from '../../../../store';
import { IRootDispatch, RootState } from '../../../../store/models';
import { useAddClientHandler } from '../hooks';
import { ProClientSelect } from './ProClientSelect';

type ProClientSelectContainerProps = {
  onClientSelected: (client: IProviderClient) => Promise<void>;
  onClose: () => void;
  isContactHidden?: (client: IProviderClient) => boolean;
  isContactDisabled?: (client: IProviderClient) => boolean;
  highlightSelectedContact?: boolean;
  hideAddClientButton?: boolean;
  keepModalOpen?: boolean;
};

export const ProClientSelectContainer: React.FC<ProClientSelectContainerProps> = ({
  onClientSelected,
  onClose,
  isContactHidden,
  isContactDisabled,
  highlightSelectedContact = true,
  hideAddClientButton = false,
  keepModalOpen = false,
}) => {
  const dispatch = useDispatch<IRootDispatch>();
  const clients = useSelector<RootState, IProviderClient[]>(
    store.select.providerClients.sortedClients,
  );
  const provider = useActiveProvider(true);
  const [routeName] = useRouteName();
  const handleClientSelected = useCallback((client: IProviderClient) => {
    onClientSelected(client);
    if (!keepModalOpen) {
      onClose();
    }
  }, [
    onClientSelected,
    onClose,
    keepModalOpen,
  ]);
  const {
    Component: ClientAdd,
    handler: handleAddClientClick,
  } = useAddClientHandler(null, handleClientSelected);

  useOnce(() => {
    dispatch.providerClients.loadAll({
      providerId: provider.id,
    });
  }, !!provider?.id || null);

  const handleClientReload = useCallback(() => (
    dispatch.providerClients.reloadAll({
      providerId: provider.id,
    })
  ), [
    provider?.id,
    dispatch.providerClients,
  ]);

  return (
    <React.Fragment>
      <ProClientSelect
        contacts={clients}
        provider={provider}
        loading={!clients}
        onContactSelect={handleClientSelected}
        onNewClientSaved={handleClientSelected}
        onClose={onClose}
        onClientReload={handleClientReload}
        isContactHidden={isContactHidden}
        isContactDisabled={isContactDisabled}
        highlightSelectedContact={highlightSelectedContact}
        hideAddClientButton={hideAddClientButton}
        onAddClientClick={handleAddClientClick}
        showAddIncentive={!!provider && routeName === PRO_CLIENT_LIST}
      />
      <ClientAdd />
    </React.Fragment>
  );
};
